/**
 * Momentum Burst — voluntary special impulse (triggered by Shift/LT/B during aiming).
 *
 * During aiming, pressing the special key opens a 1.5s burst window.
 * The active player holds a direction to lock in a burst impulse on resolve:
 * - Upward directions (UL, U, UR): cost momentum + rocketpack visuals
 * - Horizontal/Down: free + momentum surge visuals
 * - Pure down + grounded: brace heal (3%, floor 10%)
 * - No input: no impulse, return to aiming normally
 *
 * Sticky input: once you tap a direction it locks in. Press a different
 * direction to change it. Release doesn't clear. Mirrors DI's model.
 *
 * Post-burst tumble: burst resolve triggers tumble for terrain escape
 * and bouncing. After tumble expires, tank stays airborne → can aim while falling.
 */

import { Keys } from 'bonkjs';
import { BURST, BRACE, PHYSICS, TUMBLE, ACTION } from '../constants';
import { enterTumble } from '../physics/tanks';
import type { GameState, Player } from '../types';
import type { InputProvider } from '../input';
import { getGroundYBelow } from '../terrain/grid';
import { mirrorEnterTumble } from '../physics/transitions';

/**
 * Initialize burst window for a player.
 */
export function startBurstWindow(state: GameState, playerIndex: number): void {
    state.burstActivePlayer = playerIndex;
    state.burstWindowTimer = BURST.WINDOW_DURATION;
    state.burstWaitForAuthority = false;

    const p = state.players[playerIndex];
    p.burstInputX = 0;
    p.burstInputY = 0;

    // Eat any stale ACTION.FIRE from touch aim stick release — compound button
    // pulses FIRE for 50ms on release, which bleeds into burst window's fire check.
    p.fireHeld = true;

    // Special boost disabled — no free momentum on entry
}

/**
 * Per-frame burst window update. Reads directional input, counts down timer.
 * Returns true when the burst window is complete.
 */
export function updateBurstWindow(state: GameState, input: InputProvider): boolean {
    if (state.burstActivePlayer < 0) return true;

    const player = state.players[state.burstActivePlayer];

    // Read directional input (WASD + arrow keys)
    let inputX = 0;
    let inputY = 0;
    if (input.getKey(Keys.ArrowLeft) || input.getKey(Keys.A)) inputX -= 1;
    if (input.getKey(Keys.ArrowRight) || input.getKey(Keys.D)) inputX += 1;
    if (input.getKey(Keys.ArrowUp) || input.getKey(Keys.W)) inputY -= 1; // screen Y: up = negative
    if (input.getKey(Keys.ArrowDown) || input.getKey(Keys.S)) inputY += 1;

    // Brace check: pure down + grounded = heal and cancel
    if (inputX === 0 && inputY === 1) {
        const feetY = player.y + PHYSICS.ECB_RADIUS;
        const groundY = getGroundYBelow(player.x, player.y);
        if (feetY >= groundY - PHYSICS.GROUND_SNAP_TOLERANCE) {
            // Defensive burst/brace is cost-free
            if (!state.burstFromDefense) {
                player.momentum = Math.max(0, player.momentum - BURST.BRACE_MOMENTUM_COST);
            }
            player.damagePercent = Math.min(
                player.damagePercent,
                Math.max(BRACE.DAMAGE_FLOOR, player.damagePercent - BRACE.HEAL_AMOUNT)
            );
            player.squash = BRACE.SQUASH;
            player.squashVel = BRACE.SQUASH_VEL;
            player.burstBraced = true;
            player.burstInputX = 0;
            player.burstInputY = 0;
            state.burstActivePlayer = -1;
            return true;
        }
    }

    // Sticky: only update stored direction when player presses something.
    // Release doesn't clear. Press a different direction to change it.
    if (inputX !== 0 || inputY !== 0) {
        player.burstInputX = inputX;
        player.burstInputY = inputY;
    }

    // Fire cancels burst early — resolve immediately and start aiming.
    // Edge-triggered (fireHeld buffer) to avoid stale FIRE from touch aim stick release.
    // Safe in multiplayer: ACTION.FIRE is excluded from key_state relay, so only the
    // burst client triggers this. The normal burst_resolve message path syncs state.
    const fireDown = input.getKey(ACTION.FIRE);
    const fireJustPressed = fireDown && !player.fireHeld;
    player.fireHeld = fireDown;
    if (fireJustPressed) {
        resolveBurst(state, state.burstActivePlayer);
        state.burstActivePlayer = -1;
        return true;
    }

    // Count down timer
    state.burstWindowTimer -= 1 / 60; // fixed timestep

    // Pre-burst anticipation: gradually compress during last 250ms
    // Uses player state (sticky) so anticipation plays even when keys released
    if (state.burstWindowTimer > 0 && state.burstWindowTimer < BURST.ANTICIPATION_DURATION) {
        const hasDirection = player.burstInputX !== 0 || player.burstInputY !== 0;
        if (hasDirection && player.alive) {
            const progress = 1 - (state.burstWindowTimer / BURST.ANTICIPATION_DURATION);
            // Safe: physics (spring integration) is frozen during burst window
            player.squash = 1.0 + (BURST.ANTICIPATION_TARGET - 1.0) * progress;
        }
    }

    // Window expired — resolve burst
    if (state.burstWindowTimer <= 0) {
        // Non-burst client waits for authoritative burst_resolve from network
        if (state.burstWaitForAuthority) {
            if (state.burstWindowTimer >= -BURST.AUTHORITY_TIMEOUT) {
                return false; // keep waiting
            }
            console.warn('[BURST] Authority timeout — resolving locally');
            state.burstWaitForAuthority = false;
        }

        resolveBurst(state, state.burstActivePlayer);
        state.burstActivePlayer = -1;
        return true;
    }

    return false;
}

/**
 * Resolve burst — apply impulse in held direction.
 * Triggers ragdoll for terrain escape + bouncing. After ragdoll expires,
 * tank stays airborne → player can aim while falling.
 */
function resolveBurst(state: GameState, playerIndex: number): void {
    const p = state.players[playerIndex];

    const inputX = p.burstInputX;
    const inputY = p.burstInputY;

    // No direction held = no impulse
    if (inputX === 0 && inputY === 0) {
        p.burstInputX = 0;
        p.burstInputY = 0;
        return;
    }

    // Normalize direction
    const len = Math.sqrt(inputX * inputX + inputY * inputY);
    const dirX = inputX / len;
    const dirY = inputY / len;

    // Apply impulse (additive to current velocity)
    p.vx += dirX * BURST.IMPULSE_SPEED;
    p.vy += dirY * BURST.IMPULSE_SPEED;

    // Directional burst costs (inputY < 0 = up in screen coords)
    // Defensive burst is cost-free
    if (!state.burstFromDefense) {
        if (inputY < 0) {
            p.momentum = Math.max(0, p.momentum - BURST.UPWARD_MOMENTUM_COST);
        } else if (inputX !== 0) {
            p.momentum = Math.max(0, p.momentum - BURST.HORIZONTAL_MOMENTUM_COST);
        }
    }

    // Unsettle so physics moves the tank
    p.settled = false;

    // Trigger ragdoll for terrain escape + bouncing (mirrors DI's applyDeferredKnockback)
    enterTumble(p, {
        hitstunPerSpeed: BURST.TUMBLE_HITSTUN_PER_SPEED,
        minHitstun: BURST.TUMBLE_MIN_HITSTUN,
        maxHitstun: BURST.TUMBLE_MAX_HITSTUN,
        speedGated: true,
    });
    mirrorEnterTumble(p, 'burst');

    // Enable burst flight trail
    p.burstFlying = true;
    p.burstTrailCooldown = 0;

    // Squash-stretch spring for launch feel
    p.squash = BURST.RESOLVE_SQUASH;
    p.squashVel = BURST.RESOLVE_IMPULSE;

    // Clear burst input
    p.burstInputX = 0;
    p.burstInputY = 0;
}

/**
 * Get burst input direction for rendering (variable timestep).
 * Reads from player state (sticky input) so locked-in direction persists visually.
 */
export function getBurstDirection(player: Player): { x: number; y: number } {
    const x = player.burstInputX;
    const y = player.burstInputY;
    const len = Math.sqrt(x * x + y * y);
    if (len > 0.01) return { x: x / len, y: y / len };
    return { x: 0, y: 0 };
}

/**
 * Get predicted post-burst velocity for trajectory arc rendering.
 */
export function getBurstPredictedVelocity(player: Player): { vx: number; vy: number } {
    const inputX = player.burstInputX;
    const inputY = player.burstInputY;

    if (inputX === 0 && inputY === 0) {
        return { vx: player.vx, vy: player.vy };
    }

    const len = Math.sqrt(inputX * inputX + inputY * inputY);
    const dirX = inputX / len;
    const dirY = inputY / len;

    return {
        vx: player.vx + dirX * BURST.IMPULSE_SPEED,
        vy: player.vy + dirY * BURST.IMPULSE_SPEED,
    };
}
